import { createContext, useContext, useEffect, useState } from 'react'
import {
  bootPostHog,
  enableSessionRecording,
  disableSessionRecording,
  isLikelyEUUser,
} from '@/lib/posthog'

const CONSENT_KEY = 'illuminate-cookie-consent'

const CookieConsentContext = createContext()

// Resolves consent state for non-EU users — they implicitly accept
// per the privacy policy (notice + opt-out posture, standard for US
// SaaS). EU users see the banner until they choose.
function resolveInitialConsent() {
  const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(CONSENT_KEY) : null
  if (stored) return stored
  return isLikelyEUUser() ? null : 'accepted'
}

export function CookieConsentProvider({ children }) {
  const [consent, setConsent] = useState(resolveInitialConsent)

  // Boot PostHog on every mount — basic event capture is always on.
  // Session recording is layered on top via enableSessionRecording().
  useEffect(() => {
    bootPostHog()
    if (consent === 'accepted') {
      enableSessionRecording()
    }
  }, [consent])

  function acceptCookies() {
    localStorage.setItem(CONSENT_KEY, 'accepted')
    setConsent('accepted')
    bootPostHog()
    enableSessionRecording()
  }

  function declineCookies() {
    localStorage.setItem(CONSENT_KEY, 'declined')
    setConsent('declined')
    disableSessionRecording()
  }

  return (
    <CookieConsentContext.Provider value={{ consent, acceptCookies, declineCookies }}>
      {children}
    </CookieConsentContext.Provider>
  )
}

export function useCookieConsent() {
  const context = useContext(CookieConsentContext)
  if (!context) throw new Error('useCookieConsent must be used within CookieConsentProvider')
  return context
}

/** Call this anywhere to check if preferences can be saved */
export function canSavePreferences() {
  return localStorage.getItem(CONSENT_KEY) !== 'declined'
}
